import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Send, Timer } from "lucide-react";
import { toast } from "sonner";
import { BUDGET_OPTIONS } from "../../data/wallBuilder";
import PhotoField from "./PhotoField";

const API = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  "Media wall",
  "Fireplace wall",
  "Accent / slat wall",
  "Full built-in",
  "Not sure yet",
];

/**
 * Compact "Just want an estimate?" form. Reuses the /api/wall-design endpoint
 * so Rob gets a single dashboard inbox — the selections payload carries a
 * `is_quick_estimate: true` flag so he can distinguish the two paths.
 */
export default function QuickEstimate({ onBack, onSubmitted }) {
  const [form, setForm] = useState({
    city: "",
    zip: "",
    projectType: "",
    budget: "",
    special_requests: "",
    name: "",
    email: "",
    phone: "",
    photo: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.city) e.city = "Add your city.";
    if (!form.projectType) e.projectType = "Pick a project type.";
    if (!form.budget) e.budget = "Pick a budget range.";
    if (!form.name) e.name = "Add your name.";
    if (!form.email) e.email = "Add an email.";
    if (!form.phone) e.phone = "Add a phone number.";
    return e;
  };

  const submit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error(Object.values(errs)[0]);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/wall-design`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        project_address: form.city,
        preferred_contact: "phone",
        special_requests: form.special_requests,
        current_wall_photo: form.photo || null,
        inspiration_photo: null,
        selections: {
          is_quick_estimate: true,
          location: `${form.city}${form.zip ? `, ${form.zip}` : ""}`,
          layout: form.projectType,
          budget: form.budget,
          timing: "",
        },
      });
      toast.success("Estimate request sent — Rob will reach out shortly.");
      onSubmitted(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-24 grain" data-testid="quick-estimate">
      <div className="container-x px-5 md:px-10 lg:px-16 max-w-3xl">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={onBack}
            data-testid="quick-back-btn"
            className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#A1A1AA] hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Quick estimate</div>
        </div>

        <div className="mt-8">
          <h1 className="font-display text-3xl md:text-4xl leading-tight">Get an estimate in 60 seconds</h1>
          <p className="mt-2 text-sm text-[#C4C4CC] max-w-xl">
            Share the basics and Rob will call within one business day with a ballpark and next
            steps. Want a design + AI preview instead?{" "}
            <button
              type="button"
              onClick={onBack}
              className="text-[#D4AF37] underline underline-offset-2 hover:text-white"
            >
              Switch to the full designer.
            </button>
          </p>
        </div>

        <div className="mt-8 space-y-5">
          {/* Location */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="City" testid="qe-city" value={form.city} onChange={(v) => set("city", v)} error={errors.city} placeholder="Tampa" />
            <Field label="ZIP (optional)" testid="qe-zip" value={form.zip} onChange={(v) => set("zip", v)} placeholder="33602" inputMode="numeric" />
          </div>

          {/* Project type */}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">Project type</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PROJECT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`qe-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => set("projectType", t)}
                  className={`px-3.5 py-2 rounded-full text-xs border transition-colors ${
                    form.projectType === t
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3E5AB]"
                      : "border-[#27272A] text-[#D1D1D6] hover:border-[#AA8035]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            {errors.projectType && (
              <div className="text-xs text-red-400 mt-2" data-testid="error-qe-type">
                {errors.projectType}
              </div>
            )}
          </div>

          {/* Budget */}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">Budget range</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {BUDGET_OPTIONS.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  data-testid={`qe-budget-${b.value}`}
                  onClick={() => set("budget", b.label)}
                  className={`px-3.5 py-2 rounded-full text-xs border transition-colors ${
                    form.budget === b.label
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#F3E5AB]"
                      : "border-[#27272A] text-[#D1D1D6] hover:border-[#AA8035]"
                  }`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            {errors.budget && (
              <div className="text-xs text-red-400 mt-2" data-testid="error-qe-budget">
                {errors.budget}
              </div>
            )}
          </div>

          {/* Optional wall photo */}
          <PhotoField
            label="Current wall photo (optional)"
            hint="Helps Rob picture the space when he calls."
            value={form.photo}
            onChange={(v) => set("photo", v)}
            testid="qe-photo"
          />

          {/* Notes */}
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">Anything else? (optional)</label>
            <textarea
              className="input-dark mt-2 min-h-[80px]"
              rows={3}
              value={form.special_requests}
              onChange={(e) => set("special_requests", e.target.value)}
              placeholder="e.g. TV size, style ideas, timeline, rental / new build…"
              data-testid="qe-notes"
            />
          </div>

          {/* Contact */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full name" testid="qe-name" value={form.name} onChange={(v) => set("name", v)} error={errors.name} placeholder="Full name" />
            <Field label="Email" testid="qe-email" type="email" value={form.email} onChange={(v) => set("email", v)} error={errors.email} placeholder="you@example.com" />
            <Field label="Phone" testid="qe-phone" value={form.phone} onChange={(v) => set("phone", v)} error={errors.phone} placeholder="813-…" />
          </div>

          <div className="pt-4">
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="qe-submit"
              className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? "Sending…" : "Send my estimate request"}
            </button>
            <div className="mt-3 text-[11px] text-[#8b8b92] inline-flex items-center gap-1.5">
              <Timer className="w-3 h-3 text-[#D4AF37]" /> Response within one business day.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, error, type = "text", placeholder, inputMode }) {
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">{label}</label>
      <input
        className={`input-dark mt-2 ${error ? "border-red-500/70" : ""}`}
        type={type}
        placeholder={placeholder}
        value={value}
        inputMode={inputMode}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {error && (
        <div className="text-xs text-red-400 mt-1" data-testid={`error-${testid}`}>
          {error}
        </div>
      )}
    </div>
  );
}
